import { useState, useEffect, useRef } from 'react';

/**
 * Known admin capabilities (hardcoded for capability selection)
 * This list should match the capabilities registered in the backend.
 */
const KNOWN_CAPABILITIES = [
  'admin:audit:read',
  'admin:integrations:read',
  'admin:integrations:manage',
  'admin:api-keys:read',
  'admin:api-keys:manage',
  'admin:owner-dashboard:read',
  'admin:owner-dashboard:manage',
  'admin:intent:create',
  'admin:rbac:read',
  'admin:rbac:manage',
  'admin:users:read',
  'admin:users:create',
  'admin:users:update',
  'admin:users:activate',
  'admin:users:delete',
  'admin:roles:read',
  'admin:roles:create',
  'admin:roles:update',
  'admin:roles:delete',
  'admin:roles:manage-capabilities',
  'admin:rbac:assign',
  'admin:rbac:remove',
];

/**
 * AddCapabilityModal
 * 
 * Modal for adding a capability to a role with reason.
 */
export default function AddCapabilityModal({
  open,
  onClose,
  onConfirm,
  existingCapabilities = [],
  loading = false,
  error = null,
}) {
  const [selectedCapability, setSelectedCapability] = useState('');
  const [capabilitySearch, setCapabilitySearch] = useState('');
  const [reason, setReason] = useState('');
  const capabilitySelectRef = useRef(null);
  const modalRef = useRef(null);

  // Filter capabilities by search term and exclude existing ones
  const availableCapabilities = KNOWN_CAPABILITIES.filter(cap =>
    !existingCapabilities.includes(cap) &&
    cap.toLowerCase().includes(capabilitySearch.toLowerCase())
  );

  // Focus capability select when modal opens
  useEffect(() => {
    if (!open) return;
    setTimeout(() => {
      capabilitySelectRef.current?.focus();
    }, 100);
  }, [open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedCapability('');
      setCapabilitySearch('');
      setReason('');
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!selectedCapability || !reason.trim()) {
      return;
    }
    
    if (onConfirm && typeof onConfirm === 'function') {
      onConfirm(selectedCapability, reason.trim());
    }
  };

  const canSubmit = selectedCapability && reason.trim().length > 0 && !loading;

  return (
    <div
      style={styles.overlay}
      onClick={onClose}
      role="presentation"
      aria-hidden={!open}
    >
      <div
        ref={modalRef}
        style={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-capability-title"
      >
        <div style={styles.header}>
          <h2 id="add-capability-title" style={styles.title}>Add Capability</h2>
          {!loading && (
            <button
              onClick={onClose}
              style={styles.closeButton}
              aria-label="Close dialog"
              type="button"
            >
              ×
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.content}>
            <p style={styles.description}>
              Add a capability to this role. A reason is required for audit logging.
            </p>

            <div style={styles.formGroup}>
              <label htmlFor="add-capability-select" style={styles.label}>
                Capability <span style={styles.required}>*</span>
              </label>
              <input
                id="add-capability-search"
                type="text"
                value={capabilitySearch}
                onChange={(e) => setCapabilitySearch(e.target.value)}
                placeholder="Search capabilities..."
                style={styles.input}
                disabled={loading}
              />
              <select
                id="add-capability-select"
                data-testid="add-capability-select"
                ref={capabilitySelectRef}
                value={selectedCapability}
                onChange={(e) => setSelectedCapability(e.target.value)}
                style={styles.select}
                disabled={loading}
                aria-required="true"
                size={Math.min(availableCapabilities.length, 8)}
              >
                <option value="">Select a capability...</option>
                {availableCapabilities.map(cap => (
                  <option key={cap} value={cap}>{cap}</option>
                ))}
              </select>
              {availableCapabilities.length === 0 && capabilitySearch && (
                <p style={styles.helpText}>No available capabilities found matching "{capabilitySearch}"</p>
              )}
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="add-capability-reason" style={styles.label}>
                Reason <span style={styles.required}>*</span>
              </label>
              <textarea
                id="add-capability-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter a reason for adding this capability..."
                style={{
                  ...styles.textarea,
                  ...(!reason.trim() && styles.textareaError),
                }}
                rows={4}
                disabled={loading}
                aria-required="true"
              />
              {!reason.trim() && (
                <p style={styles.errorText}>Reason is required</p>
              )}
              <p style={styles.helpText}>
                This reason will be recorded in the audit log.
              </p>
            </div>

            {error && (
              <div style={styles.error} role="alert" aria-live="polite">
                <p>{error}</p>
              </div>
            )}
          </div>

          <div style={styles.footer}>
            <button
              onClick={onClose}
              disabled={loading}
              style={styles.cancelButton}
              type="button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit || loading}
              style={{
                ...styles.submitButton,
                ...((!canSubmit || loading) && styles.submitButtonDisabled),
              }}
              aria-disabled={!canSubmit || loading}
            >
              {loading ? 'Adding...' : 'Add Capability'}
              data-testid="add-capability-submit-button"
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '0.5rem',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
    width: '90%',
    maxWidth: '540px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden', // Prevent modal from scrolling, only content scrolls
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: '1px solid #e5e7eb',
    flexShrink: 0, // Header doesn't shrink
  },
  title: {
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#111827',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    color: '#6b7280',
    cursor: 'pointer',
    padding: '0.25rem 0.5rem',
    lineHeight: 1,
    borderRadius: '0.25rem',
    transition: 'background-color 0.2s',
  },
  content: {
    padding: '1.5rem',
    flex: 1,
    overflowY: 'auto',
    minHeight: 0, // Allow flex item to shrink below content size
  },
  description: {
    marginTop: 0,
    marginBottom: '1.5rem',
    color: '#6b7280',
    fontSize: '0.875rem',
  },
  formGroup: {
    marginBottom: '1.5rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 500,
    color: '#374151',
    fontSize: '0.875rem',
  },
  required: {
    color: '#ef4444',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    fontFamily: 'inherit',
    backgroundColor: '#fff',
    color: '#111827',
    marginBottom: '0.5rem',
    transition: 'border-color 0.2s',
  },
  select: {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    fontFamily: 'monospace',
    backgroundColor: '#fff',
    color: '#111827',
    transition: 'border-color 0.2s',
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '100px',
    backgroundColor: '#fff',
    color: '#111827',
    lineHeight: '1.5',
    transition: 'border-color 0.2s',
  },
  textareaError: {
    borderColor: '#ef4444',
  },
  errorText: {
    marginTop: '0.5rem',
    fontSize: '0.75rem',
    color: '#ef4444',
  },
  helpText: {
    marginTop: '0.5rem',
    fontSize: '0.75rem',
    color: '#6b7280',
    lineHeight: '1.4',
  },
  error: {
    marginTop: '1rem',
    padding: '0.75rem',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    border: '1px solid #ef4444',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    padding: '1.5rem',
    borderTop: '1px solid #e5e7eb',
    flexShrink: 0, // Footer doesn't shrink, stays visible
    backgroundColor: '#fff', // Ensure footer has background for sticky effect
  },
  cancelButton: {
    padding: '0.5rem 1rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    backgroundColor: '#fff',
    color: '#374151',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  submitButton: {
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: '0.375rem',
    backgroundColor: '#10b981',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  submitButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    backgroundColor: '#9ca3af',
  },
};

